import React, { useState, useEffect } from "react";
import { Mail, Phone, MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageBanner from "@/components/PageBanner";
import WhatsAppIcon from "@/components/WhatsAppIcon";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [intlContact, setIntlContact] = useState(null);

  useEffect(() => {
    api.get("/site-settings").then((res) => setIntlContact(res.data.international_contact)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-[70vh] bg-[#FDFCFB]">
      <PageBanner pageKey="contact" eyebrow="Get In Touch" title="Contact Us" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24 pt-10 sm:pt-0 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <div className="space-y-5">
            <div className="flex items-center gap-3"><MapPin size={20} className="text-[var(--brand-primary)]" /> <span className="text-[var(--brand-secondary)]">Ground Floor, Classic Ashiyana II, Nazarbagh, Lucknow - 226001, Uttar Pradesh, India</span></div>
            <div className="flex items-center gap-3"><Phone size={20} className="text-[var(--brand-primary)]" /> <span className="text-[var(--brand-secondary)]">+91 73143 44673</span></div>
            <div className="flex items-center gap-3"><Mail size={20} className="text-[var(--brand-primary)]" /> <span className="text-[var(--brand-secondary)]">help@gulfudaan.com</span></div>
            {intlContact?.phone && (
              <div data-testid="contact-international-number" className="flex items-center gap-3 pt-3 border-t border-[#E5E1DA] mt-2">
                <WhatsAppIcon size={20} className="text-[#25D366]" />
                <span className="text-[var(--brand-secondary)]">
                  <a href={`https://wa.me/${intlContact.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--brand-primary)]">{intlContact.phone}</a>
                  {" "}<span className="text-xs text-[#9a9a9a]">({intlContact.label || "Outside India"})</span>
                </span>
              </div>
            )}
          </div>
        </div>
        <form data-testid="contact-form" onSubmit={handleSubmit} className="p-8 rounded-lg border border-[#E5E1DA] bg-white space-y-4">
          <Input data-testid="contact-name-input" placeholder="Your Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="focus-visible:ring-[var(--brand-primary)]" />
          <Input data-testid="contact-email-input" type="email" placeholder="Your Email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="focus-visible:ring-[var(--brand-primary)]" />
          <Input data-testid="contact-phone-input" placeholder="Phone Number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="focus-visible:ring-[var(--brand-primary)]" />
          <Textarea data-testid="contact-message-input" placeholder="Your Message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="focus-visible:ring-[var(--brand-primary)]" />
          <Button data-testid="contact-submit-btn" type="submit" disabled={submitting} className="w-full bg-[var(--brand-primary)] hover:bg-[#9E855A] text-white h-12">
            {submitting ? <Loader2 className="animate-spin mr-2" size={18} /> : null} Send Message
          </Button>
        </form>
      </div>
    </div>
  );
}
